import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useLang } from '../context/LangContext';

const LanguageDropdownContainer = styled.div`
  position: relative;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
`;

const LanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  z-index: 1000;
  overflow: hidden;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  margin-top: 8px;
`;

const LanguageOption = styled.div<{ $isSelected?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  background: ${({ $isSelected }) => $isSelected ? '#f5f5f5' : 'white'};
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

interface LanguageSelectorProps {
  onLanguageChange?: (language: 'ko' | 'en') => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  onLanguageChange
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { lang, setLang } = useLang();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLanguageSelect = (language: 'ko' | 'en') => {
    setLang(language);
    setIsDropdownOpen(false);
    onLanguageChange?.(language);
  };

  const getLanguageDisplay = (language: 'ko' | 'en') => {
    return language === 'ko' ? '한국어 (KR)' : '영어 (ENG)';
  };

  return (
    <LanguageDropdownContainer ref={dropdownRef}>
      <IconButton onClick={handleLanguageToggle}>
        <Icon src="/common/lang.svg" alt="Language" />
      </IconButton>
      <LanguageDropdown $isOpen={isDropdownOpen}>
        <LanguageOption 
          $isSelected={lang === 'ko'} 
          onClick={() => handleLanguageSelect('ko')}
        >
          {getLanguageDisplay('ko')}
        </LanguageOption>
        <LanguageOption 
          $isSelected={lang === 'en'} 
          onClick={() => handleLanguageSelect('en')}
        >
          {getLanguageDisplay('en')}
        </LanguageOption>
      </LanguageDropdown>
    </LanguageDropdownContainer>
  );
};

export default LanguageSelector;
